import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { profileReducer, initialState, ACTIONS, selectors } from './profileReducer';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

// Inline sync function to avoid import issues
const syncCreditAllocationsInline = async (userId) => {
  if (!userId) return { success: false, error: 'No userId' };
  
  try {
    // Get all credit card allocations for this user
    const { data: allocations, error: allocError } = await supabase
      .from('credit_card_allocations')
      .select('*')
      .eq('user_id', userId);

    if (allocError) throw allocError;

    // Group allocations by bundle_id and subscription_id
    const bundleAllocations = new Map();
    const subscriptionAllocations = new Map();

    (allocations || []).forEach(alloc => {
      if (alloc.bundle_id) {
        const current = bundleAllocations.get(alloc.bundle_id) || 0;
        bundleAllocations.set(alloc.bundle_id, current + (parseFloat(alloc.amount_allocated) || 0));
      }
      if (alloc.subscription_id) {
        const current = subscriptionAllocations.get(alloc.subscription_id) || 0;
        subscriptionAllocations.set(alloc.subscription_id, current + (parseFloat(alloc.amount_allocated) || 0));
      }
    });

    // Update user_bundles
    const { data: userBundles } = await supabase
      .from('user_bundles')
      .select('id, bundle_code, monthly_cost, credit_applied, actual_cost')
      .eq('user_id', userId)
      .eq('is_active', true);

    const bundleUpdates = [];
    for (const bundle of (userBundles || [])) {
      const creditApplied = bundleAllocations.get(bundle.id) || 0;
      const monthlyCost = parseFloat(bundle.monthly_cost) || 0;
      const actualCost = Math.max(0, monthlyCost - creditApplied);

      if (
        parseFloat(bundle.credit_applied) !== creditApplied ||
        parseFloat(bundle.actual_cost) !== actualCost
      ) {
        bundleUpdates.push({ id: bundle.id, bundle_code: bundle.bundle_code });
        await supabase
          .from('user_bundles')
          .update({ credit_applied: creditApplied, actual_cost: actualCost })
          .eq('id', bundle.id);
      }
    }

    // Update subscriptions
    const { data: subscriptions } = await supabase
      .from('subscriptions')
      .select('id, service_name, monthly_cost, credit_applied, actual_cost')
      .eq('user_id', userId);

    const subscriptionUpdates = [];
    for (const sub of (subscriptions || [])) {
      const creditApplied = subscriptionAllocations.get(sub.id) || 0;
      const monthlyCost = parseFloat(sub.monthly_cost) || 0;
      const actualCost = Math.max(0, monthlyCost - creditApplied);

      if (
        parseFloat(sub.credit_applied) !== creditApplied ||
        parseFloat(sub.actual_cost) !== actualCost
      ) {
        subscriptionUpdates.push({ id: sub.id, service_name: sub.service_name });
        await supabase
          .from('subscriptions')
          .update({ credit_applied: creditApplied, actual_cost: actualCost })
          .eq('id', sub.id);
      }
    }

    return {
      success: true,
      bundleUpdates,
      subscriptionUpdates,
      summary: {
        bundlesUpdated: bundleUpdates.length,
        subscriptionsUpdated: subscriptionUpdates.length,
      }
    };
  } catch (error) {
    console.error('Error syncing credit allocations:', error);
    return { success: false, error: error.message };
  }
};

const ProfileContext = createContext(null);

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children, userId }) => {
  const [state, dispatch] = useReducer(profileReducer, initialState);

  // ==================
  // Data Loading
  // ==================
  const loadProfile = useCallback(async () => {
    if (!userId) return;
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });

    try {
      // Load user profile
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      // Load credit cards
      const { data: creditCards } = await supabase
        .from('user_credit_cards')
        .select('*')
        .eq('user_id', userId);

      // Load memberships
      const { data: memberships } = await supabase
        .from('user_memberships')
        .select('*')
        .eq('user_id', userId);

      // Load subscriptions
      const { data: subscriptions } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId);

      // Load allocations
      const cardIds = creditCards?.map(c => c.id) || [];
      let allocations = [];
      if (cardIds.length > 0) {
        const { data: allocData } = await supabase
          .from('credit_card_allocations')
          .select('*')
          .in('credit_card_id', cardIds);
        allocations = allocData || [];
      }

      // Load active perks
      const { data: activePerks } = await supabase
        .from('active_perks')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true);

      // Load user bundles WITH reference data (bundle name, services, etc.)
      const { data: userBundles } = await supabase
        .from('user_bundles')
        .select('*, bundles(bundle_name, bundle_code, monthly_cost, notes, bundle_services(service_name, included_tier))')
        .eq('user_id', userId)
        .eq('is_active', true);

      console.log('Loaded user bundles with reference data:', userBundles);

      dispatch({
        type: ACTIONS.LOAD_PROFILE,
        payload: {
          profile: {
            userId,
            email: userData.email || '',
            fullName: userData.full_name || '',
            zipCode: userData.zip_code || '',
            internetProvider: userData.internet_provider || '',
            internetPlan: userData.internet_plan || '',
            internetMonthlyCost: userData.internet_monthly_cost || '',
            cellularProvider: userData.cellular_provider || '',
            cellularPlan: userData.cellular_plan || '',
            cellularMonthlyCost: userData.cellular_monthly_cost || '',
            cellularLines: userData.cellular_lines || 1,
            planType: userData.plan_type || 'free',
            queriesUsedMonth: userData.queries_used_month || 0,
            eligibility: {
              isStudent: userData.is_student || false,
              isSenior: userData.is_senior || false,
              isVeteran: userData.is_veteran || false,
              isFirstResponder: userData.is_first_responder || false,
            },
          },
          creditCards: creditCards || [],
          memberships: memberships || [],
          subscriptions: subscriptions || [],
          allocations,
          activePerks: activePerks || [],
          bundles: userBundles || [],
        },
      });
      
      // After loading, sync credit allocations to ensure consistency
      // Do this silently (no toast) on initial load
      const syncResult = await syncCreditAllocationsInline(userId);
      if (syncResult.success && (syncResult.bundleUpdates?.length > 0 || syncResult.subscriptionUpdates?.length > 0)) {
        console.log('Credit sync applied updates:', syncResult.summary);
        // Reload the profile data to reflect synced values
        const { data: updatedBundles } = await supabase
          .from('user_bundles')
          .select('*, bundles(bundle_name, bundle_code, monthly_cost, notes, bundle_services(service_name, included_tier))')
          .eq('user_id', userId)
          .eq('is_active', true);
        
        const { data: updatedSubscriptions } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', userId);
        
        // Update state with synced data
        dispatch({
          type: ACTIONS.LOAD_PROFILE,
          payload: {
            profile: {
              userId,
              email: userData.email || '',
              fullName: userData.full_name || '',
              zipCode: userData.zip_code || '',
              internetProvider: userData.internet_provider || '',
              internetPlan: userData.internet_plan || '',
              internetMonthlyCost: userData.internet_monthly_cost || '',
              cellularProvider: userData.cellular_provider || '',
              cellularPlan: userData.cellular_plan || '',
              cellularMonthlyCost: userData.cellular_monthly_cost || '',
              cellularLines: userData.cellular_lines || 1,
              planType: userData.plan_type || 'free',
              queriesUsedMonth: userData.queries_used_month || 0,
              eligibility: {
                isStudent: userData.is_student || false,
                isSenior: userData.is_senior || false,
                isVeteran: userData.is_veteran || false,
                isFirstResponder: userData.is_first_responder || false,
              },
            },
            creditCards: creditCards || [],
            memberships: memberships || [],
            subscriptions: updatedSubscriptions || subscriptions || [],
            allocations,
            activePerks: activePerks || [],
            bundles: updatedBundles || userBundles || [],
          },
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      toast.error('Failed to load profile');
    }
  }, [userId]);

  const loadReferenceData = useCallback(async () => {
    try {
      // Load streaming services (without tiers join since table doesn't exist)
      const { data: streamingServices } = await supabase
        .from('streaming_services')
        .select('*')
        .eq('is_active', true)
        .order('service_name');

      const { data: ispPerks } = await supabase
        .from('isp_perks')
        .select('*')
        .eq('is_active', true);

      const { data: cardPerks } = await supabase
        .from('card_perks')
        .select('*')
        .eq('is_active', true);

      const { data: membershipPerks } = await supabase
        .from('membership_perks')
        .select('*')
        .eq('is_active', true);

      const { data: bundles } = await supabase
        .from('bundles')
        .select('*, bundle_services(*)')
        .eq('is_active', true);

      // Load memberships reference data (for pricing lookup)
      const { data: membershipsRef } = await supabase
        .from('memberships')
        .select('*')
        .eq('is_active', true);

      // Enrich streaming services with fallback prices if no tiers loaded
      const enrichedStreamingServices = (streamingServices || []).map(service => {
        // If service has tiers from DB, use them
        if (service.service_tiers && service.service_tiers.length > 0) {
          const standardTier = service.service_tiers.find(t => 
            t.tier_name?.toLowerCase() === 'standard' || 
            t.tier_name?.toLowerCase() === 'basic'
          ) || service.service_tiers[0];
          
          return {
            ...service,
            base_price_monthly: standardTier?.monthly_price || standardTier?.price || 0,
            tiers: service.service_tiers,
          };
        }
        
        // Otherwise use fallback prices
        const fallbackPrices = {
          'netflix': { base: 15.49, premium: 22.99, ads: 6.99 },
          'disney+': { base: 9.99, premium: 15.99, ads: 7.99 },
          'hulu': { base: 17.99, premium: 17.99, ads: 7.99 },
          'amazon prime video': { base: 8.99, premium: 8.99 },
          'apple tv+': { base: 9.99, premium: 9.99 },
          'max (hbo max)': { base: 15.99, premium: 20.99, ads: 9.99 },
          'max': { base: 15.99, premium: 20.99, ads: 9.99 },
          'paramount+': { base: 11.99, premium: 11.99, ads: 5.99 },
          'peacock': { base: 7.99, premium: 13.99, ads: 0 },
          'discovery+': { base: 8.99, premium: 8.99, ads: 4.99 },
          'espn+': { base: 10.99, premium: 10.99 },
          'youtube tv': { base: 72.99, premium: 72.99 },
          'apple music': { base: 10.99, premium: 10.99 },
          'apple arcade': { base: 6.99, premium: 6.99 },
          'apple news+': { base: 12.99, premium: 12.99 },
          'apple fitness+': { base: 9.99, premium: 9.99 },
          'icloud+': { base: 2.99, premium: 9.99 },
          'spotify': { base: 10.99, premium: 10.99 },
          'showtime': { base: 10.99, premium: 10.99 },
          'starz': { base: 9.99, premium: 9.99 },
          'amc+': { base: 8.99, premium: 8.99 },
          'crunchyroll': { base: 7.99, premium: 14.99 },
        };
        
        const fallback = fallbackPrices[service.service_name?.toLowerCase()] || { base: 0, premium: 0, ads: 0 };
        
        return {
          ...service,
          base_price_monthly: fallback.base,
          premium_price_monthly: fallback.premium,
          ad_supported_price: fallback.ads,
          tiers: [
            ...(fallback.ads > 0 ? [{ tier_name: 'With Ads', monthly_price: fallback.ads }] : []),
            { tier_name: 'Standard', monthly_price: fallback.base },
            ...(fallback.premium !== fallback.base ? [{ tier_name: 'Premium', monthly_price: fallback.premium }] : []),
          ],
        };
      });

      console.log('Enriched streaming services with prices:', enrichedStreamingServices.slice(0, 3));

      dispatch({
        type: ACTIONS.LOAD_REFERENCE_DATA,
        payload: {
          streamingServices: enrichedStreamingServices,
          ispPerks: ispPerks || [],
          cardPerks: cardPerks || [],
          membershipPerks: membershipPerks || [],
          bundles: bundles || [],
          membershipsRef: membershipsRef || [],
        },
      });
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  }, []);

  // ==================
  // Save Operations
  // ==================
  const saveProfile = useCallback(async (overrideData = null) => {
    dispatch({ type: ACTIONS.SET_SAVING, payload: true });

    try {
      // Use override data if provided, otherwise use state
      const profileData = overrideData || state.profile;
      const { error } = await supabase
        .from('users')
        .update({
          zip_code: profileData.zipCode,
          internet_provider: profileData.internetProvider,
          internet_plan: profileData.internetPlan,
          internet_monthly_cost: profileData.internetMonthlyCost,
          cellular_provider: profileData.cellularProvider,
          cellular_plan: profileData.cellularPlan,
          cellular_monthly_cost: profileData.cellularMonthlyCost,
          cellular_lines: profileData.cellularLines,
          is_student: profileData.eligibility?.isStudent ?? state.profile.eligibility.isStudent,
          is_senior: profileData.eligibility?.isSenior ?? state.profile.eligibility.isSenior,
          is_veteran: profileData.eligibility?.isVeteran ?? state.profile.eligibility.isVeteran,
          is_first_responder: profileData.eligibility?.isFirstResponder ?? state.profile.eligibility.isFirstResponder,
          updated_at: new Date().toISOString(),
          last_active_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) throw error;

      // If we used override data, sync to local state without marking dirty
      if (overrideData) {
        dispatch({ 
          type: ACTIONS.SYNC_PROFILE_FROM_SAVE, 
          payload: {
            zipCode: overrideData.zipCode,
            internetProvider: overrideData.internetProvider,
            internetPlan: overrideData.internetPlan,
            internetMonthlyCost: overrideData.internetMonthlyCost,
            cellularProvider: overrideData.cellularProvider,
            cellularPlan: overrideData.cellularPlan,
            cellularMonthlyCost: overrideData.cellularMonthlyCost,
            cellularLines: overrideData.cellularLines,
          }
        });
      } else {
        // Just clear dirty flags if saving existing state
        dispatch({ type: ACTIONS.CLEAR_SECTION_DIRTY, payload: 'profile' });
        dispatch({ type: ACTIONS.CLEAR_SECTION_DIRTY, payload: 'isp' });
        dispatch({ type: ACTIONS.CLEAR_SECTION_DIRTY, payload: 'cellular' });
      }

      dispatch({ type: ACTIONS.CLEAR_SECTION_DIRTY, payload: 'eligibility' });
      toast.success('Profile saved!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      dispatch({ type: ACTIONS.SET_SAVING, payload: false });
    }
  }, [state, userId]);

  const saveCreditCard = useCallback(async (card) => {
    dispatch({ type: ACTIONS.SET_SAVING, payload: true });

    try {
      if (card.id && !card.id.startsWith('temp_')) {
        // Update existing
        const { error } = await supabase
          .from('user_credit_cards')
          .update({
            card_name: card.card_name,
            issuer: card.issuer,
            streaming_credit_monthly: card.streaming_credit_monthly,
          })
          .eq('id', card.id);

        if (error) throw error;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('user_credit_cards')
          .insert({
            user_id: userId,
            card_name: card.card_name,
            issuer: card.issuer,
            streaming_credit_monthly: card.streaming_credit_monthly || 0,
          })
          .select()
          .single();

        if (error) throw error;

        // Update state with real ID
        dispatch({
          type: ACTIONS.UPDATE_CREDIT_CARD,
          payload: { ...card, id: data.id },
        });
      }

      dispatch({ type: ACTIONS.CLEAR_SECTION_DIRTY, payload: 'creditCards' });
      toast.success('Credit card saved!');
    } catch (error) {
      console.error('Error saving credit card:', error);
      toast.error('Failed to save credit card');
    } finally {
      dispatch({ type: ACTIONS.SET_SAVING, payload: false });
    }
  }, [userId]);

  const deleteCreditCard = useCallback(async (cardId) => {
    try {
      // Delete allocations first
      await supabase
        .from('credit_card_allocations')
        .delete()
        .eq('credit_card_id', cardId);

      // Delete card
      const { error } = await supabase
        .from('user_credit_cards')
        .delete()
        .eq('id', cardId);

      if (error) throw error;

      dispatch({
        type: ACTIONS.REMOVE_CREDIT_CARD,
        payload: { cardId, skipImpactCheck: true },
      });

      toast.success('Credit card removed');
    } catch (error) {
      console.error('Error deleting credit card:', error);
      toast.error('Failed to remove credit card');
    }
  }, []);

  const saveMembership = useCallback(async (membership) => {
    dispatch({ type: ACTIONS.SET_SAVING, payload: true });

    try {
      if (membership.id && !membership.id.startsWith('temp_')) {
        // Update existing
        const { error } = await supabase
          .from('user_memberships')
          .update({
            membership_name: membership.membership_name,
            membership_type: membership.membership_type,
            monthly_cost: membership.monthly_cost || 0,
            annual_cost: membership.annual_cost || 0,
          })
          .eq('id', membership.id);

        if (error) throw error;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('user_memberships')
          .insert({
            user_id: userId,
            membership_name: membership.membership_name,
            membership_type: membership.membership_type,
            monthly_cost: membership.monthly_cost || 0,
            annual_cost: membership.annual_cost || 0,
          })
          .select()
          .single();

        if (error) throw error;

        dispatch({
          type: ACTIONS.UPDATE_MEMBERSHIP,
          payload: { ...membership, id: data.id },
        });
      }

      dispatch({ type: ACTIONS.CLEAR_SECTION_DIRTY, payload: 'memberships' });
      toast.success('Membership saved!');
    } catch (error) {
      console.error('Error saving membership:', error);
      toast.error('Failed to save membership');
    } finally {
      dispatch({ type: ACTIONS.SET_SAVING, payload: false });
    }
  }, [userId]);

  const deleteMembership = useCallback(async (membershipId) => {
    try {
      const { error } = await supabase
        .from('user_memberships')
        .delete()
        .eq('id', membershipId);

      if (error) throw error;

      dispatch({
        type: ACTIONS.REMOVE_MEMBERSHIP,
        payload: { membershipId, skipImpactCheck: true },
      });

      toast.success('Membership removed');
    } catch (error) {
      console.error('Error deleting membership:', error);
      toast.error('Failed to remove membership');
    }
  }, []);

  const saveSubscription = useCallback(async (subscription) => {
    dispatch({ type: ACTIONS.SET_SAVING, payload: true });

    try {
      if (subscription.id && !subscription.id.startsWith('temp_')) {
        // Update existing
        const { error } = await supabase
          .from('subscriptions')
          .update({
            service_name: subscription.service_name,
            subscription_tier: subscription.subscription_tier,
            monthly_cost: subscription.monthly_cost,
            billing_cycle: subscription.billing_cycle,
            payment_method: subscription.payment_method,
            payment_source: subscription.payment_source,
            credit_applied: subscription.credit_applied,
            actual_cost: subscription.actual_cost,
          })
          .eq('id', subscription.id);

        if (error) throw error;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('subscriptions')
          .insert({
            user_id: userId,
            service_name: subscription.service_name,
            subscription_tier: subscription.subscription_tier,
            monthly_cost: subscription.monthly_cost || 0,
            billing_cycle: subscription.billing_cycle || 'monthly',
            payment_method: subscription.payment_method || 'paid',
            payment_source: subscription.payment_source,
            credit_applied: subscription.credit_applied || 0,
            actual_cost: subscription.actual_cost || subscription.monthly_cost || 0,
          })
          .select()
          .single();

        if (error) throw error;

        dispatch({
          type: ACTIONS.UPDATE_SUBSCRIPTION,
          payload: { ...subscription, id: data.id },
        });
      }

      dispatch({ type: ACTIONS.CLEAR_SECTION_DIRTY, payload: 'subscriptions' });
      toast.success('Subscription saved!');
    } catch (error) {
      console.error('Error saving subscription:', error);
      toast.error('Failed to save subscription');
    } finally {
      dispatch({ type: ACTIONS.SET_SAVING, payload: false });
    }
  }, [userId]);

  const deleteSubscription = useCallback(async (subscriptionId) => {
    try {
      // Delete allocations first
      await supabase
        .from('credit_card_allocations')
        .delete()
        .eq('subscription_id', subscriptionId);

      // Delete subscription
      const { error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('id', subscriptionId);

      if (error) throw error;

      dispatch({ type: ACTIONS.REMOVE_SUBSCRIPTION, payload: subscriptionId });
      toast.success('Subscription removed');
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast.error('Failed to remove subscription');
    }
  }, []);

  const saveAllocation = useCallback(async (cardId, subscriptionId, amount) => {
    try {
      if (amount === 0 || amount === null || amount === '') {
        // Remove all allocations for this card+subscription combo
        await supabase
          .from('credit_card_allocations')
          .delete()
          .eq('credit_card_id', cardId)
          .eq('subscription_id', subscriptionId);
      } else {
        // Delete any existing allocations first, then insert fresh
        // This avoids duplicates from stale state
        await supabase
          .from('credit_card_allocations')
          .delete()
          .eq('credit_card_id', cardId)
          .eq('subscription_id', subscriptionId);

        // Insert new allocation
        await supabase
          .from('credit_card_allocations')
          .insert({
            user_id: userId,
            credit_card_id: cardId,
            subscription_id: subscriptionId,
            amount_allocated: amount,
            target_type: 'subscription',
          });
      }

      // Update subscription credit_applied - get fresh total from DB
      const { data: allAllocations } = await supabase
        .from('credit_card_allocations')
        .select('amount_allocated')
        .eq('subscription_id', subscriptionId);

      const totalCredit = (allAllocations || []).reduce(
        (sum, a) => sum + parseFloat(a.amount_allocated || 0), 0
      );

      const sub = state.subscriptions.find(s => s.id === subscriptionId);
      if (sub) {
        await supabase
          .from('subscriptions')
          .update({
            credit_applied: totalCredit,
            actual_cost: Math.max(0, (sub.monthly_cost || 0) - totalCredit),
          })
          .eq('id', subscriptionId);
      }

      // Update local state
      dispatch({
        type: ACTIONS.UPDATE_CARD_ALLOCATION,
        payload: { cardId, subscriptionId, amount },
      });
    } catch (error) {
      console.error('Error saving allocation:', error);
      toast.error('Failed to save allocation');
    }
  }, [state.allocations, state.subscriptions, userId]);

  // Save a new bundle
  const saveBundle = useCallback(async (bundleData) => {
    try {
      const { data, error } = await supabase
        .from('user_bundles')
        .insert({
          user_id: userId,
          bundle_code: bundleData.bundle_code,
          monthly_cost: bundleData.monthly_cost || 0,
          payment_method: bundleData.payment_method || 'paid',
          payment_source: bundleData.payment_source || null,
          credit_applied: bundleData.credit_applied || 0,
          actual_cost: bundleData.actual_cost || bundleData.monthly_cost || 0,
        })
        .select()
        .single();

      if (error) throw error;

      dispatch({
        type: ACTIONS.ADD_BUNDLE,
        payload: data,
      });

      // Clear dirty state since we just saved to database
      dispatch({ type: ACTIONS.CLEAR_SECTION_DIRTY, payload: 'bundles' });

      toast.success('Bundle added!');
      return data;
    } catch (error) {
      console.error('Error saving bundle:', error);
      toast.error('Failed to save bundle');
      throw error;
    }
  }, [userId]);

  // Update existing bundle (e.g., change monthly cost)
  const updateBundle = useCallback(async (bundleId, updates) => {
    try {
      console.log('updateBundle called:', { bundleId, updates });
      
      const bundle = state.bundles.find(b => b.id === bundleId);
      if (!bundle) {
        console.error('Bundle not found:', bundleId);
        return;
      }

      // If updating monthly_cost, also recalculate actual_cost
      const updateData = { ...updates };
      if (updates.monthly_cost !== undefined) {
        const creditApplied = parseFloat(bundle.credit_applied) || 0;
        updateData.actual_cost = Math.max(0, parseFloat(updates.monthly_cost) - creditApplied);
      }

      console.log('Updating user_bundles with:', updateData);

      const { data, error } = await supabase
        .from('user_bundles')
        .update(updateData)
        .eq('id', bundleId)
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Supabase update successful:', data);

      // Update local state
      dispatch({
        type: ACTIONS.UPDATE_BUNDLE,
        payload: { id: bundleId, ...updateData },
      });

      toast.success('Bundle updated!');
      
      // Reload profile to ensure state is in sync
      await loadProfile();
      
      return { ...bundle, ...updateData };
    } catch (error) {
      console.error('Error updating bundle:', error);
      toast.error('Failed to update bundle');
      throw error;
    }
  }, [state.bundles, loadProfile]);

  // FIXED: Save bundle credit allocation - NOW ALSO SAVES TO credit_card_allocations
  const saveBundleAllocation = useCallback(async (bundleId, creditApplied, cardId = null) => {
    try {
      const bundle = state.bundles.find(b => b.id === bundleId);
      if (!bundle) {
        console.error('Bundle not found:', bundleId);
        return;
      }

      const actualCost = Math.max(0, (bundle.monthly_cost || 0) - creditApplied);

      // Update user_bundles table
      await supabase
        .from('user_bundles')
        .update({
          credit_applied: creditApplied,
          actual_cost: actualCost,
        })
        .eq('id', bundleId);

      // FIXED: Also manage credit_card_allocations record for this bundle
      // First, delete any existing allocation for this bundle (from any card)
      await supabase
        .from('credit_card_allocations')
        .delete()
        .eq('bundle_id', bundleId);

      // If there's a credit amount and a card, create the allocation record
      if (creditApplied > 0 && cardId) {
        await supabase
          .from('credit_card_allocations')
          .insert({
            user_id: userId,
            credit_card_id: cardId,
            bundle_id: bundleId,
            subscription_id: null,
            amount_allocated: creditApplied,
            target_type: 'bundle',
          });
      }

      // Update local state
      dispatch({
        type: ACTIONS.UPDATE_BUNDLE_ALLOCATION,
        payload: { bundleId, creditApplied, actualCost },
      });

      // Clear dirty state since we just saved to database
      dispatch({ type: ACTIONS.CLEAR_SECTION_DIRTY, payload: 'bundles' });
      
      // Don't show individual toast - CreditCardsSection shows one at the end
    } catch (error) {
      console.error('Error saving bundle allocation:', error);
      toast.error('Failed to save bundle allocation');
    }
  }, [state.bundles, userId]);

  const saveActivePerk = useCallback(async (perkData) => {
    try {
      const { perk_source, service_name, tier, choice_group } = perkData;
      console.log('Saving active perk:', { perk_source, service_name, tier, choice_group, userId });

      // If this is a choice perk, first deactivate any other perks in the same choice group
      if (choice_group) {
        // Get all perks in this choice group from reference data
        const choicePerks = state.refData.membershipPerks?.filter(
          p => p.choice_group === choice_group
        ) || [];

        const otherServices = choicePerks
          .filter(p => p.service_name !== service_name)
          .map(p => p.service_name);

        // Deactivate other choice perks in active_perks table
        if (otherServices.length > 0) {
          const { error: deactivateError } = await supabase
            .from('active_perks')
            .update({ is_active: false })
            .eq('user_id', userId)
            .eq('perk_source', perk_source)
            .in('service_name', otherServices);

          if (deactivateError) {
            console.error('Error deactivating other perks:', deactivateError);
          }

          // Also update subscriptions for deactivated perks back to 'paid'
          for (const otherService of otherServices) {
            const otherSub = state.subscriptions.find(
              s => s.service_name?.toLowerCase() === otherService?.toLowerCase()
            );

            if (otherSub && otherSub.payment_method === 'membership_benefit') {
              await supabase
                .from('subscriptions')
                .update({
                  payment_method: 'paid',
                  payment_source: '',
                })
                .eq('id', otherSub.id);
            }
          }
        }
      }

      // Check if this perk already exists (use maybeSingle to avoid error when not found)
      const { data: existingPerk, error: selectError } = await supabase
        .from('active_perks')
        .select('*')
        .eq('user_id', userId)
        .eq('perk_source', perk_source)
        .eq('service_name', service_name)
        .maybeSingle();

      if (selectError) {
        console.error('Error checking existing perk:', selectError);
      }

      if (existingPerk) {
        // Update existing perk
        console.log('Updating existing perk:', existingPerk.id);
        const { error: updateError } = await supabase
          .from('active_perks')
          .update({ 
            is_active: true, 
            tier: tier,
            activated_date: new Date().toISOString().split('T')[0]
          })
          .eq('id', existingPerk.id);

        if (updateError) {
          console.error('Error updating perk:', updateError);
          throw updateError;
        }
      } else {
        // Insert new perk - generate UUID for id
        console.log('Inserting new perk');
        const newId = crypto.randomUUID();
        const { error: insertError } = await supabase
          .from('active_perks')
          .insert({
            id: newId,
            user_id: userId,
            perk_source: perk_source,
            service_name: service_name,
            tier: tier,
            activated_date: new Date().toISOString().split('T')[0],
            is_active: true,
          });

        if (insertError) {
          console.error('Error inserting perk:', insertError);
          throw insertError;
        }
      }

      // Also update the subscription if it exists
      const matchingSub = state.subscriptions.find(
        s => s.service_name?.toLowerCase() === service_name?.toLowerCase()
      );

      if (matchingSub) {
        await supabase
          .from('subscriptions')
          .update({
            payment_method: 'membership_benefit',
            payment_source: perk_source,
            monthly_cost: 0,
            actual_cost: 0,
          })
          .eq('id', matchingSub.id);
      }

      // Reload profile to get updated data
      await loadProfile();

      toast.success(`${service_name} perk activated!`);
    } catch (error) {
      console.error('Error saving active perk:', error);
      toast.error('Failed to save perk');
    }
  }, [userId, state.refData.membershipPerks, state.subscriptions, loadProfile]);

  const deactivateActivePerk = useCallback(async (perkSource, serviceName) => {
    try {
      await supabase
        .from('active_perks')
        .update({ is_active: false })
        .eq('user_id', userId)
        .eq('perk_source', perkSource)
        .eq('service_name', serviceName);

      // Also update the subscription if it exists
      const matchingSub = state.subscriptions.find(
        s => s.service_name?.toLowerCase() === serviceName?.toLowerCase()
      );

      if (matchingSub && matchingSub.payment_method === 'membership_benefit') {
        await supabase
          .from('subscriptions')
          .update({
            payment_method: 'paid',
            payment_source: '',
          })
          .eq('id', matchingSub.id);
      }

      await loadProfile();
      toast.success(`${serviceName} perk deactivated`);
    } catch (error) {
      console.error('Error deactivating perk:', error);
      toast.error('Failed to deactivate perk');
    }
  }, [userId, state.subscriptions, loadProfile]);

  const reconcileData = useCallback(async () => {
    try {
      toast.loading('Syncing payment methods...', { id: 'sync' });

      const response = await fetch('/api/reconcile-subscriptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();

      if (data.success) {
        await loadProfile();

        const { summary } = data;
        const changes = (summary?.perksApplied || 0) + (summary?.creditsApplied || 0) + (summary?.bundlesDetected || 0);

        if (changes > 0) {
          const parts = [];
          if (summary.perksApplied > 0) parts.push(`${summary.perksApplied} perk${summary.perksApplied > 1 ? 's' : ''}`);
          if (summary.creditsApplied > 0) parts.push(`${summary.creditsApplied} credit${summary.creditsApplied > 1 ? 's' : ''}`);
          if (summary.bundlesDetected > 0) parts.push(`${summary.bundlesDetected} bundle${summary.bundlesDetected > 1 ? 's' : ''}`);
          toast.success(`Applied ${parts.join(', ')}!`, { id: 'sync' });
        } else {
          toast.success('Everything is up to date!', { id: 'sync' });
        }
      } else {
        toast.error(data.message || 'Failed to sync data', { id: 'sync' });
      }
    } catch (error) {
      console.error('Reconciliation error:', error);
      toast.error('Error syncing data. Please try again.', { id: 'sync' });
    }
  }, [userId, loadProfile]);

  // Sync credit allocations to ensure data consistency
  const syncCredits = useCallback(async (showToast = true) => {
    if (!userId) return { success: false };
    
    try {
      if (showToast) {
        toast.loading('Syncing credit allocations...', { id: 'credit-sync' });
      }
      
      // Sync the data
      const result = await syncCreditAllocationsInline(userId);
      
      if (result.success) {
        // Reload profile to get updated data
        await loadProfile();
        
        if (showToast) {
          const { bundlesUpdated, subscriptionsUpdated } = result.summary;
          if (bundlesUpdated > 0 || subscriptionsUpdated > 0) {
            toast.success(
              `Synced ${bundlesUpdated} bundle${bundlesUpdated !== 1 ? 's' : ''} and ${subscriptionsUpdated} subscription${subscriptionsUpdated !== 1 ? 's' : ''}`, 
              { id: 'credit-sync' }
            );
          } else {
            toast.success('Credits already in sync!', { id: 'credit-sync' });
          }
        }
      } else {
        if (showToast) {
          toast.error('Failed to sync credits', { id: 'credit-sync' });
        }
      }
      
      return result;
    } catch (error) {
      console.error('Credit sync error:', error);
      if (showToast) {
        toast.error('Error syncing credits', { id: 'credit-sync' });
      }
      return { success: false, error: error.message };
    }
  }, [userId, loadProfile]);

  // ==================
  // Action Dispatchers
  // ==================
  const updateISP = useCallback((newISP) => {
    dispatch({ type: ACTIONS.UPDATE_ISP, payload: { newISP } });
  }, []);

  const updateZipCode = useCallback((zipCode) => {
    dispatch({ type: ACTIONS.UPDATE_ZIP_CODE, payload: zipCode });
  }, []);

  const updateInternetPlan = useCallback((plan) => {
    dispatch({ type: ACTIONS.UPDATE_PROFILE_FIELD, payload: { field: 'internetPlan', value: plan } });
  }, []);

  const updateInternetMonthlyCost = useCallback((cost) => {
    dispatch({ type: ACTIONS.UPDATE_PROFILE_FIELD, payload: { field: 'internetMonthlyCost', value: cost } });
  }, []);

  const updateEligibility = useCallback((eligibility) => {
    dispatch({ type: ACTIONS.UPDATE_ELIGIBILITY, payload: eligibility });
  }, []);

  const addCreditCard = useCallback((card) => {
    const tempCard = { ...card, id: `temp_${Date.now()}` };
    dispatch({ type: ACTIONS.ADD_CREDIT_CARD, payload: tempCard });
    return tempCard;
  }, []);

  const removeCreditCard = useCallback((cardId) => {
    dispatch({ type: ACTIONS.REMOVE_CREDIT_CARD, payload: { cardId } });
  }, []);

  const addMembership = useCallback((membership) => {
    const tempMembership = { ...membership, id: `temp_${Date.now()}` };
    dispatch({ type: ACTIONS.ADD_MEMBERSHIP, payload: tempMembership });
    return tempMembership;
  }, []);

  const removeMembership = useCallback((membershipId) => {
    dispatch({ type: ACTIONS.REMOVE_MEMBERSHIP, payload: { membershipId } });
  }, []);

  const updateMembership = useCallback((membership) => {
    dispatch({ type: ACTIONS.UPDATE_MEMBERSHIP, payload: membership });
  }, []);

  const addSubscription = useCallback((subscription) => {
    const tempSub = { ...subscription, id: `temp_${Date.now()}` };
    dispatch({ type: ACTIONS.ADD_SUBSCRIPTION, payload: tempSub });
    return tempSub;
  }, []);

  const removeSubscription = useCallback((subscriptionId) => {
    dispatch({ type: ACTIONS.REMOVE_SUBSCRIPTION, payload: subscriptionId });
  }, []);

  const updateSubscription = useCallback((subscription) => {
    dispatch({ type: ACTIONS.UPDATE_SUBSCRIPTION, payload: subscription });
  }, []);

  const toggleSection = useCallback((section) => {
    dispatch({ type: ACTIONS.TOGGLE_SECTION, payload: section });
  }, []);

  const confirmPendingChanges = useCallback(() => {
    dispatch({ type: ACTIONS.CONFIRM_PENDING_CHANGES });
  }, []);

  const cancelPendingChanges = useCallback(() => {
    dispatch({ type: ACTIONS.CANCEL_PENDING_CHANGES });
  }, []);

  // ==================
  // Computed Values
  // ==================
  const totalMonthlyCost = selectors.getTotalMonthlyCost(state);
  const totalCreditsAvailable = selectors.getTotalCreditsAvailable(state);
  const totalCreditsAllocated = selectors.getTotalCreditsAllocated(state);
  const unusedCredits = selectors.getUnusedCredits(state);

  const value = {
    // State
    ...state,

    // Computed values
    totalMonthlyCost,
    totalCreditsAvailable,
    totalCreditsAllocated,
    unusedCredits,

    // Data loading
    loadProfile,
    loadReferenceData,

    // Save operations
    saveProfile,
    saveCreditCard,
    deleteCreditCard,
    saveMembership,
    deleteMembership,
    saveSubscription,
    deleteSubscription,
    saveAllocation,
    saveBundle,
    updateBundle,
    saveBundleAllocation,
    saveActivePerk,
    deactivateActivePerk,
    reconcileData,
    syncCredits,

    // Action dispatchers
    updateISP,
    updateZipCode,
    updateInternetPlan,
    updateInternetMonthlyCost,
    updateEligibility,
    addCreditCard,
    removeCreditCard,
    addMembership,
    removeMembership,
    updateMembership,
    addSubscription,
    removeSubscription,
    updateSubscription,
    toggleSection,
    confirmPendingChanges,
    cancelPendingChanges,

    // Selectors
    getCardAllocations: (cardId) => selectors.getCardAllocations(state, cardId),
    isSectionDirty: (section) => selectors.isSectionDirty(state, section),
    getServicesForCreditAllocation: () => selectors.getServicesForCreditAllocation(state),
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};